/**
 * Inventory Items Service Types
 *
 * Types for managing estate inventory items (assets) in matters
 *
 * BACKEND SCHEMA NOTES:
 * - GET /matters/:matterId/inventory-items returns InventoryItem[] directly (not wrapped)
 * - POST/PUT use camelCase format for institutionId, institutionName, institutionEmail
 * - Backend returns type as object {name, slug, iconUrl} not string
 * - Value and description are NOT included - must be extracted from itemInfo
 * - See backend-schema.md for complete API documentation
 */

import { z } from 'zod';
import type { Handling } from '@/lib/services/matter-handling/types';
import type { ExtractedInstitutionMatch } from '@/lib/services/ai-analysis/types';

// Core Types

/**
 * Inventory item status - matches backend InventoryItemStatus enum
 */
export type InventoryItemStatus =
  | 'draft'
  | 'notified'
  | 'identified'
  | 'not-held'
  | 'collected'
  | 'resolved'
  | 'archived'
  | 'confirmed'
  | 'settled'
  | 'not-owed';

/**
 * Statuses in which money is tied to the asset, so its institution can no
 * longer be changed. Mirrors the backend's `MONEY_FINALISED_STATUSES` in
 * `InventoryItemsService.assertInstitutionChangeAllowed`, which rejects a
 * facility change with `400 INSTITUTION_CHANGE_BLOCKED_TERMINAL`.
 *
 * A facility change IS permitted throughout the active workflow — `draft`,
 * `notified` and `identified` — because a notified facility routinely redirects
 * the executor to its head office and the lawyer must re-point the asset
 * (LF-5319). Gate on this set, never on `status === 'draft'`: mirroring the
 * long-removed draft-only rule silently discarded the change on exactly the two
 * statuses Discovery is mostly made of (LF-6251).
 *
 * Keep in step with the backend constant.
 */
export const MONEY_FINALISED_STATUSES: readonly InventoryItemStatus[] = [
  'collected',
  'resolved',
  'settled'
];

/** True when money is tied to the asset, so its institution is locked. */
export function isMoneyFinalisedStatus(
  status: InventoryItemStatus | undefined
): boolean {
  return !!status && MONEY_FINALISED_STATUSES.includes(status);
}

/**
 * Inventory item types as defined by backend
 */
export type InventoryItemType =
  | 'superannuation'
  | 'bank-account'
  | 'srn-shares'
  | 'hin-shares'
  | 'nursing-home-bond'
  | 'real-estate'
  | 'other'
  | 'vehicle'
  | 'life-insurance'
  | 'cryptocurrency'
  | 'managed-funds'
  | 'private-company'
  // Liability types
  | 'funeral-burial'
  | 'legal-professional'
  | 'mortgage'
  | 'vehicle-loan'
  | 'tax-liability'
  | 'credit-card'
  | 'personal-loan'
  | 'utility-bill'
  | 'estate-expense'
  | 'other-liability';

/**
 * Set of all liability type slugs — single source of truth.
 * Import this everywhere instead of defining locally.
 */
export const LIABILITY_TYPE_SLUGS: ReadonlySet<string> = new Set([
  'funeral-burial',
  'legal-professional',
  'mortgage',
  'vehicle-loan',
  'tax-liability',
  'credit-card',
  'personal-loan',
  'utility-bill',
  'estate-expense',
  'other-liability'
]);

/**
 * Set of share type slugs — used to detect share items.
 */
export const SHARE_TYPE_SLUGS: ReadonlySet<string> = new Set([
  'srn-shares',
  'hin-shares'
]);

// Share Workflow Types

/**
 * Ownership type for share holdings.
 * Determines estate involvement and available instructions.
 */
export type ShareOwnershipType = 'solely-held' | 'joint-tenants' | 'not-held';

/**
 * Instruction for a share holding.
 * Drives form curation and correspondence.
 */
export type ShareInstruction =
  | 'sell'
  | 'transfer-to-beneficiary'
  | 'surviving-joint-holder'
  | 'undecided';

/**
 * Beneficiary allocation for transfer instructions.
 * Backend embeds contact names in responses; writes only need contactId + units.
 */
export interface ShareTransferBeneficiary {
  contactId: number;
  contact?: {
    id: number;
    firstName: string;
    lastName: string;
    organisationName?: string | null;
  };
  units: number;
}

export const SHARE_INSTRUCTION_LABELS: Record<ShareInstruction, string> = {
  sell: 'Sell',
  'transfer-to-beneficiary': 'Transfer to Beneficiary',
  'surviving-joint-holder': 'Surviving Joint Holder',
  undecided: 'Undecided'
};

/**
 * Vehicle disposition instructions (LF-3204).
 * Mirrors property's pattern. `scrap-or-dispose` is unique to vehicles.
 */
export type VehicleInstruction =
  | 'sell'
  | 'transfer-to-beneficiary'
  | 'scrap-or-dispose'
  | 'undecided';

export const VEHICLE_INSTRUCTION_LABELS: Record<VehicleInstruction, string> = {
  sell: 'Sell',
  'transfer-to-beneficiary': 'Transfer to Beneficiary',
  'scrap-or-dispose': 'Scrap or Dispose',
  undecided: 'Undecided'
};

export const SHARE_OWNERSHIP_LABELS: Record<
  Exclude<ShareOwnershipType, 'not-held'>,
  string
> = {
  'solely-held': 'Solely Held',
  'joint-tenants': 'Jointly Held'
};

/**
 * Get available instructions for a given ownership type.
 */
export function getAvailableShareInstructions(
  isNonEstate: boolean | undefined
): ShareInstruction[] {
  if (isNonEstate) return ['surviving-joint-holder'];
  return ['sell', 'transfer-to-beneficiary', 'undecided'];
}

/**
 * Inventory item type categories for grouping
 */
export type InventoryItemCategory =
  | 'real-estate'
  | 'bank-accounts'
  | 'investments'
  | 'vehicles'
  | 'personal-property'
  | 'business-interests'
  | 'life-insurance'
  | 'superannuation'
  | 'debts'
  | 'other';

/**
 * Inventory item type metadata (for frontend use)
 */
export interface InventoryItemTypeMetadata {
  id: number;
  name: string;
  slug: string;
  type: InventoryItemType;
  category: InventoryItemCategory;
  description?: string;
  requiresValuation: boolean;
  sortOrder: number;
}

/**
 * Type details returned by backend
 */
export interface InventoryItemTypeDetails {
  name: string;
  slug: string;
  iconUrl?: string;
  category?: 'asset' | 'liability';
  linkedAssetType?: InventoryItemType;
}

/**
 * Resolution plan for a secured liability — drives whether the debt reserves
 * estate cash (Locke King's Act). Backend derives this from the linked
 * asset's instruction (mortgages from the linked property, vehicle-loans
 * from the linked vehicle).
 */
export type MortgageResolutionPlan =
  | 'property-sale'
  | 'beneficiary-refinances'
  | 'estate-pays'
  | 'undecided';

export type VehicleLoanResolutionPlan =
  | 'vehicle-sale'
  | 'beneficiary-assumes'
  | 'estate-pays'
  | 'undecided';

/**
 * LF-3250 — audit field present on the four allow-listed liability types
 * (`credit-card`, `personal-loan`, `utility-bill`, `other-liability`) once
 * an institution payout has settled them. The inventory list uses this to
 * render the "Settled by {institution} payout on {date}" attribution chip.
 */
export interface SettledByInstitutionPayoutInfo {
  matterInstitutionPayoutId: number;
  /** ISO timestamp. */
  settledAt: string;
}

/**
 * Current-value confirmation provenance (LF-4356). Present when an item's
 * current value has been confirmed against a source. Mirrors the backend
 * `CurrentValueConfirmationDto` (shared by the item read, the set/clear
 * endpoint response, and the Current View BFF row).
 */
export interface CurrentValueConfirmation {
  /** Free-text source note; null when the source is a linked document only. */
  source: string | null;
  /** File.id of the linked matter document; null when the source is a note only. */
  sourceFileId: number | null;
  /**
   * Display name of the linked source document (displayName ?? originalFilename),
   * so the UI can render "Confirmed against {document}". Null when the source is a
   * free-text note only, or the file was removed.
   */
  sourceFileName: string | null;
  /** Confirmer's display name (falls back to email, then null if the user was removed). */
  by: string | null;
  /** ISO timestamp of when the confirmation was recorded. */
  at: string;
  /**
   * True when the ✓ was stamped by an AI review→import gesture rather than typed
   * by hand (LF-5317). Lets the caption / indicator distinguish AI-assisted from
   * hand-verified.
   */
  aiAssisted: boolean;
  /**
   * True when the value is confirmed by the daily market-price feed rather than a
   * person (LF-5355) — a share priced from the market and not manually overridden.
   * `by`/`source`/`sourceFileId` are null and `at` is the last price-update time.
   * Rendered as "Confirmed · live market data" with no manual confirm control.
   */
  market: boolean;
}

/**
 * Request body for confirming a current value (LF-4356). At least one of
 * `source` / `sourceFileId` must be supplied.
 */
export interface SetValueConfirmationRequest {
  source?: string;
  sourceFileId?: number;
}

/**
 * Response of the set/clear current-value confirmation endpoints (LF-4356).
 */
export interface ValueConfirmationResponse {
  inventoryItemId: number;
  currentValueConfirmed: boolean;
  currentValueConfirmation: CurrentValueConfirmation | null;
}

/**
 * A selectable institution-response attachment offered as a value-confirmation
 * source in the manual "Link a document" picker (LF-5393).
 */
export interface ValueConfirmationSourceFile {
  id: number;
  displayName: string | null;
  originalFilename: string;
}

/**
 * Attachments from one inbound institution response, labelled with provenance
 * (institution name + the date the response was received — LF-5393).
 */
export interface ValueConfirmationSourceGroup {
  correspondenceId: number;
  institutionName: string;
  receivedAt: string | null;
  files: ValueConfirmationSourceFile[];
}

/**
 * Value-confirmation source options scoped to a single inventory item (LF-5393)
 * — institution-response attachments the manual picker can cite. Empty when the
 * item has no institution or no responses carry attachments.
 */
export interface ValueConfirmationSourcesResponse {
  institutionResponses: ValueConfirmationSourceGroup[];
}

/**
 * Inventory item entity - matches backend InventoryItem entity
 */
export interface InventoryItem {
  id: number;
  type: InventoryItemType | InventoryItemTypeDetails; // Backend returns object with type details
  status: InventoryItemStatus;
  category?: 'asset' | 'liability';
  isNonEstate?: boolean;
  estateReference?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;

  // JSONB field for type-specific data
  // JSONB blob whose shape is item-type-dependent; callers narrow per `type`
  // discriminator. Switching to `unknown` cascades into widespread unsafe-access
  // errors.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemInfo?: Record<string, any> | null;

  /**
   * External handling recorded on the item ITSELF — only ever the institution-less
   * types, a house or a car (EH-CR-007). An item with an institution inherits from
   * it and a DB CHECK keeps its own handler null, so this is null for those.
   *
   * Optional because not every inventory response projects it (create/update
   * replies and the several narrower projections do not), so a caller must treat
   * absent and "not handled" alike rather than assuming the field is there.
   */
  handling?: Handling | null;

  /**
   * The handling that **governs** this item: its institution's where it has one
   * (EH-CR-003's read-time inheritance), its own where it does not (VALE-1760).
   *
   * **Render badges from this, not from `handling` directly above.** That one is
   * the item's own handler and is null for every asset sitting at a bank, which is
   * why the Inventory page showed nothing for assets at handed-over institutions.
   *
   * Optional for the same reason `handling` is: only the routes that run the
   * inheritance enrichment project it (the matter inventory list does). A caller
   * that may see either should prefer this and fall back to `handling`.
   */
  effectiveHandling?: Handling | null;

  // Relations
  matterId?: number;
  matter?: { id: number };
  institutionId?: number;
  institution?: {
    id: number;
    name: string;
    type: string;
    faviconUrl?: string | null;
    email?: string;
    isCustom?: boolean;
    stages?: Array<{
      stage: 'discovery' | 'representation' | 'collection';
      completionState: 'complete' | 'in_progress' | 'not_started';
      statusDetail?: string;
    }>;
  };
  /**
   * **How the AI matched this row's institution** (VALE-1468). Present ONLY on a
   * pending extract row, never on a persisted one: nothing stores it, and the backend
   * ignores it on apply. Declared here because the AI review renders pending rows
   * through this same type, so a row that carries it needs somewhere to say so.
   *
   * Read `confidence`; the note on `ExtractedInstitutionMatch` says why the score is
   * not a threshold.
   */
  institutionMatch?: ExtractedInstitutionMatch;
  payeeContactId?: number | null;
  payeeContact?: { id: number; firstName: string; lastName: string } | null;
  /**
   * The receipt evidencing that `payeeContactId` paid this debt out of pocket, and the
   * date they paid it. Both are facts about the out-of-pocket payment rather than about
   * the debt, and `paidByOn` is NOT the reimbursement date — that belongs to the round
   * that eventually pays them back.
   */
  paidByEvidenceFileId?: number | null;
  paidByOn?: string | null;
  /**
   * True when `payeeContactId` paid this debt FOR the estate (a reimbursement);
   * false when they ARE the creditor. Stored, never inferred from whether an
   * institution is linked — that proxy answers a different question.
   */
  paidOutOfPocket?: boolean;
  linkedInventoryItemId?: number | null;
  linkedInventoryItem?: {
    id: number;
    type: string;
    description: string;
    value: number | null;
  } | null;

  // Current-value confirmation (LF-4356). Projected onto the standard item read
  // so the drawer form can render its read-mode caption + pre-fill the edit
  // control regardless of which surface opened it.
  currentValueConfirmed?: boolean;
  currentValueConfirmation?: CurrentValueConfirmation | null;

  // Date-of-death value confirmation (LF-5546). Tracked independently of the
  // current-value confirmation above; `market` is always false for DOD.
  dodValueConfirmed?: boolean;
  dodValueConfirmation?: CurrentValueConfirmation | null;

  // Frontend-only fields for UI display (populated from itemInfo or calculations)
  description?: string;
  value?: number;
  valuationDate?: string;
  valuationSource?: string;

  /**
   * Current value — the same lens the Current View surfaces; null when no
   * current figure is recorded. Read `currentValue ?? value` for "current,
   * falling back to date-of-death" (the LF-5869 convention).
   */
  currentValue?: number | null;
}

/**
 * Inventory item summary for list views
 */
export interface InventoryItemSummary {
  id: number;
  description: string;
  value: number;
  status: InventoryItemStatus;
  type: string;
  category: InventoryItemCategory;
  institution?: string;
}

// Request/Response Types

/**
 * Create inventory item request
 */
export interface CreateInventoryItemRequest {
  type: string; // Using string to match backend slug format
  institution?: { id: number }; // Backend expects institution object with id
  institutionId?: number; // Direct institutionId for existing institutions
  institutionName?: string; // For creating new institutions
  institutionEmail?: string; // For creating new institutions
  /**
   * The institution type the reader chose on the picker's create step
   * (VALE-1577). Sent only alongside `institutionName`, and only from the
   * `Other` / "Organisation" picker, which is the one place the type is not
   * already implied by the item type. Absent, the backend derives it from the
   * inventory item type exactly as before.
   */
  institutionType?: string;
  /**
   * Existing institution ids acknowledged as near-duplicates of `institutionName`
   * (VALE-1422). Sent only on a re-submit after a 409 `NearDuplicateInstitution`.
   */
  acknowledgedDuplicateInstitutionIds?: number[];
  isNonEstate?: boolean;
  estateReference?: string;
  itemInfo?: Record<string, unknown>;
  payeeContactId?: number;
  paidByEvidenceFileId?: number;
  paidByOn?: string;
  paidOutOfPocket?: boolean;
  linkedInventoryItemId?: number;
  /*
    Value-confirmation intents applied straight after the create (VALE-355), so a
    source known at the moment a figure is entered no longer needs a second visit to
    the item. Same shape and rules as the update path's — at least one of
    source/sourceFileId, and the figure has to exist.
  */
  currentValueConfirmation?: SetValueConfirmationRequest;
  dodValueConfirmation?: SetValueConfirmationRequest;
}

/**
 * Update inventory item request
 */
export interface UpdateInventoryItemRequest {
  type?: string; // Using string to match backend slug format
  institution?: { id: number } | null; // Backend expects institution object with id
  institutionId?: number | null; // Direct institutionId for existing institutions
  institutionName?: string; // For creating new institutions
  institutionEmail?: string; // For creating new institutions
  /**
   * The institution type the reader chose on the picker's create step
   * (VALE-1577). Sent only alongside `institutionName`, and only from the
   * `Other` / "Organisation" picker, which is the one place the type is not
   * already implied by the item type. Absent, the backend derives it from the
   * inventory item type exactly as before.
   */
  institutionType?: string;
  /**
   * Existing institution ids acknowledged as near-duplicates of `institutionName`
   * (VALE-1422). Sent only on a re-submit after a 409 `NearDuplicateInstitution`.
   */
  acknowledgedDuplicateInstitutionIds?: number[];
  isNonEstate?: boolean;
  status?: InventoryItemStatus;
  estateReference?: string;
  itemInfo?: Record<string, unknown>;
  payeeContactId?: number | null;
  paidByEvidenceFileId?: number | null;
  paidByOn?: string | null;
  /**
   * True when `payeeContactId` paid this debt FOR the estate (a reimbursement);
   * false when they ARE the creditor. Stored, never inferred from whether an
   * institution is linked — that proxy answers a different question.
   */
  paidOutOfPocket?: boolean;
  linkedInventoryItemId?: number | null;
  // Value-confirmation intents folded into the item Save (LF-5546). An object
  // sets/confirms (at least one of source/sourceFileId required); `null` clears
  // back to Estimate; omit (undefined) to leave the confirmation untouched.
  currentValueConfirmation?: SetValueConfirmationRequest | null;
  dodValueConfirmation?: SetValueConfirmationRequest | null;
  /**
   * When the money this update realises arrived, as `YYYY-MM-DD`. Sent together
   * with an `itemInfo.realisedValue` — the "mark collected, enter the amount"
   * moment — so the backend records a dated receipt for the distribution
   * statement. Omit it and the realised value still saves.
   */
  receivedAt?: string;
}

/**
 * Inventory items list response
 */
export interface InventoryItemsListResponse {
  items: InventoryItem[];
  total: number;
  totalValue: number;
  byCategory: Record<
    InventoryItemCategory,
    {
      count: number;
      value: number;
    }
  >;
}

/**
 * Status filter options for inventory items
 * Matches backend's InventoryItemStatusFilter enum
 */
export type InventoryStatusFilter = 'active' | 'archived' | 'all';

/**
 * Inventory item filters
 */
export interface InventoryItemFilters {
  category?: InventoryItemCategory;
  status?: InventoryStatusFilter; // Changed to use backend's filter type (active/archived/all)
  institutionId?: number;
  isNonEstate?: boolean;
  search?: string;
  minValue?: number;
  maxValue?: number;
}

// Validation Schemas

export const InventoryItemStatusSchema = z.enum([
  'draft',
  'notified',
  'identified',
  'not-held',
  'collected',
  'resolved',
  'archived',
  'confirmed',
  'settled',
  'not-owed'
]);

export const InventoryItemTypeSchema = z.enum([
  'superannuation',
  'bank-account',
  'srn-shares',
  'hin-shares',
  'nursing-home-bond',
  'real-estate',
  'other',
  'vehicle',
  'life-insurance',
  'cryptocurrency',
  'managed-funds',
  'private-company',
  // Liability types
  'funeral-burial',
  'legal-professional',
  'mortgage',
  'vehicle-loan',
  'tax-liability',
  'credit-card',
  'personal-loan',
  'utility-bill',
  'estate-expense',
  'other-liability'
]);

export const InventoryItemCategorySchema = z.enum([
  'real-estate',
  'bank-accounts',
  'investments',
  'vehicles',
  'personal-property',
  'business-interests',
  'life-insurance',
  'superannuation',
  'debts',
  'other'
]);

export const CreateInventoryItemRequestSchema = z.object({
  type: z.string(), // Accept string for backend slug format
  institution: z.object({ id: z.number().int().positive() }).optional(),
  institutionId: z.number().int().positive().optional(), // Direct institutionId for backend
  institutionName: z.string().min(1).optional(), // Required if provided
  institutionEmail: z.string().email().optional(), // Required valid email if provided
  // Declared, or the client validator strips it from the body and the chosen
  // type never reaches the backend — `safeParse` returns only declared keys, and
  // `validation.data` is what is sent (VALE-1577).
  institutionType: z.string().optional(),
  isNonEstate: z.boolean().optional(),
  estateReference: z.string().optional(),
  itemInfo: z.record(z.string(), z.unknown()).optional(),
  payeeContactId: z.number().int().positive().optional(),
  // Declared for the same reason as `institutionType` above: `safeParse` returns
  // ONLY declared keys and `validation.data` is what is sent, so an undeclared
  // field is stripped silently — no type error, no failing request, the value
  // simply never reaches the backend.
  paidByEvidenceFileId: z.number().int().positive().optional(),
  paidByOn: z.string().optional(),
  paidOutOfPocket: z.boolean().optional(),
  linkedInventoryItemId: z.number().int().positive().optional(),
  // Declared, or the client validator strips them out of the POST body and the
  // backend never records the confirmation — the same silent-strip trap the update
  // schema's own comment calls out below (VALE-355).
  currentValueConfirmation: z
    .object({
      source: z.string().optional(),
      sourceFileId: z.number().optional()
    })
    .optional(),
  dodValueConfirmation: z
    .object({
      source: z.string().optional(),
      sourceFileId: z.number().optional()
    })
    .optional()
});

export const UpdateInventoryItemRequestSchema = z.object({
  type: InventoryItemTypeSchema.optional(),
  status: InventoryItemStatusSchema.optional(),
  institution: z
    .object({ id: z.number().int().positive() })
    .nullable()
    .optional(),
  institutionId: z.number().int().positive().nullable().optional(), // Direct institutionId for backend
  institutionName: z.string().optional(),
  institutionEmail: z.string().email().optional(),
  // Declared, or the client validator strips it from the body and the chosen
  // type never reaches the backend — `safeParse` returns only declared keys, and
  // `validation.data` is what is sent (VALE-1577).
  institutionType: z.string().optional(),
  isNonEstate: z.boolean().optional(),
  estateReference: z.string().optional(),
  itemInfo: z.record(z.string(), z.unknown()).optional(),
  payeeContactId: z.number().int().positive().nullable().optional(),
  // Declared for the same reason as `institutionType` above: `safeParse` returns
  // ONLY declared keys and `validation.data` is what is sent, so an undeclared
  // field is stripped silently — no type error, no failing request, the value
  // simply never reaches the backend.
  paidByEvidenceFileId: z.number().int().positive().nullable().optional(),
  paidByOn: z.string().nullable().optional(),
  paidOutOfPocket: z.boolean().optional(),
  linkedInventoryItemId: z.number().int().positive().nullable().optional(),
  // Value-confirmation intents folded into Save (LF-5546) — object sets/confirms,
  // null clears, omit leaves untouched. Optional+nullable so the client validator
  // preserves them through to the PUT body instead of stripping them.
  currentValueConfirmation: z
    .object({
      source: z.string().optional(),
      sourceFileId: z.number().optional()
    })
    .nullable()
    .optional(),
  dodValueConfirmation: z
    .object({
      source: z.string().optional(),
      sourceFileId: z.number().optional()
    })
    .nullable()
    .optional(),
  // When the money this update realises arrived (`YYYY-MM-DD`). Must be declared
  // here or the validator strips it out of the PUT body and the backend never
  // records the receipt — the same silent-strip trap the confirmation intents
  // above call out.
  receivedAt: z.string().optional()
});

export const InventoryStatusFilterSchema = z.enum([
  'active',
  'archived',
  'all'
]);

export const InventoryItemFiltersSchema = z.object({
  category: InventoryItemCategorySchema.optional(),
  status: InventoryStatusFilterSchema.optional(), // Use backend's filter schema
  institutionId: z.number().int().positive().optional(),
  search: z.string().optional(),
  minValue: z.number().min(0).optional(),
  maxValue: z.number().min(0).optional()
});

// Type to Category Mapping

export const TYPE_TO_CATEGORY: Record<
  InventoryItemType,
  InventoryItemCategory
> = {
  superannuation: 'superannuation',
  'bank-account': 'bank-accounts',
  'srn-shares': 'investments',
  'hin-shares': 'investments',
  'nursing-home-bond': 'other',
  'real-estate': 'real-estate',
  other: 'other',
  vehicle: 'vehicles',
  'life-insurance': 'life-insurance',
  cryptocurrency: 'investments',
  'managed-funds': 'investments',
  'private-company': 'business-interests',
  // Liability types
  'funeral-burial': 'debts',
  'legal-professional': 'debts',
  mortgage: 'debts',
  'vehicle-loan': 'debts',
  'tax-liability': 'debts',
  'credit-card': 'debts',
  'personal-loan': 'debts',
  'utility-bill': 'debts',
  'estate-expense': 'debts',
  'other-liability': 'debts'
};

// Category Metadata

export interface CategoryMetadata {
  label: string;
  description: string;
  icon: string;
  color: string;
  defaultTypes: string[];
}

export const CATEGORY_METADATA: Record<
  InventoryItemCategory,
  CategoryMetadata
> = {
  'real-estate': {
    label: 'Real Estate',
    description: 'Property and land assets',
    icon: 'Home',
    color: 'blue',
    defaultTypes: ['residential-property', 'commercial-property', 'vacant-land']
  },
  'bank-accounts': {
    label: 'Bank Accounts',
    description: 'Savings, checking, and term deposits',
    icon: 'Banknote',
    color: 'green',
    defaultTypes: ['savings-account', 'checking-account', 'term-deposit']
  },
  investments: {
    label: 'Investments',
    description: 'Stocks, bonds, and managed funds',
    icon: 'TrendingUp',
    color: 'purple',
    defaultTypes: ['shares', 'bonds', 'managed-funds', 'etfs']
  },
  vehicles: {
    label: 'Vehicles',
    description: 'Cars, boats, and other vehicles',
    icon: 'Car',
    color: 'orange',
    defaultTypes: ['car', 'motorcycle', 'boat', 'caravan']
  },
  'personal-property': {
    label: 'Personal Property',
    description: 'Jewelry, art, collectibles, and household items',
    icon: 'Package',
    color: 'pink',
    defaultTypes: ['jewelry', 'artwork', 'collectibles', 'furniture']
  },
  'business-interests': {
    label: 'Business Interests',
    description: 'Business ownership and partnerships',
    icon: 'Briefcase',
    color: 'indigo',
    defaultTypes: ['sole-proprietor', 'partnership', 'company-shares']
  },
  'life-insurance': {
    label: 'Life Insurance',
    description: 'Life insurance policies and payouts',
    icon: 'Shield',
    color: 'teal',
    defaultTypes: ['term-life', 'whole-life', 'endowment']
  },
  superannuation: {
    label: 'Superannuation',
    description: 'Retirement and pension accounts',
    icon: 'PiggyBank',
    color: 'amber',
    defaultTypes: ['accumulation', 'pension', 'self-managed']
  },
  debts: {
    label: 'Debts & Liabilities',
    description: 'Loans, mortgages, and other debts',
    icon: 'CreditCard',
    color: 'red',
    defaultTypes: ['mortgage', 'personal-loan', 'credit-card', 'tax-debt']
  },
  other: {
    label: 'Other Assets',
    description: 'Miscellaneous assets',
    icon: 'Archive',
    color: 'gray',
    defaultTypes: ['other']
  }
};

// ============================================================================
// Current View BFF (LF-2813) — `GET /matters/:matterId/inventory-items/current-view`
//
// The "DoD / Current" toggle's Current View. Two-section Outstanding → Distributed
// model. Shapes are verbatim from the BE handover §2 (LF-3901-fe-handover.md) /
// technical-specs v1.4. Consume directly — the BFF resolves the balance, totals,
// proceeds-row labels, and status-tag labels server-side (do not recompute).
// ============================================================================

/**
 * Status-derived tag on a row. The first group is on an Outstanding row (assets)
 * or `unsettled` (liabilities). The terminal group (`not-held` / `archived` /
 * `not-owed`) is carried by rows in the on-demand Excluded section (LF-4576).
 */
export type OutstandingItemStatusTag =
  | 'draft'
  | 'notified'
  | 'awaiting-sale'
  | 'pending-transfer'
  | 'pending-collection'
  | 'awaiting-instruction'
  | 'unsettled'
  | 'not-held'
  | 'archived'
  | 'not-owed';

/** One row in the Trust Account drilldown — a proceeds source or the final deduction. */
export interface ProceedsRowDto {
  id: string;
  kind:
    | 'institution-proceeds'
    | 'item-sale'
    // Money in that no inventory item accounts for. The rows above are the asset
    // lens and cannot see it, while the balance below counts it — so without this
    // the drilldown does not sum to its own total.
    | 'other-receipts'
    // The creditor half of what left trust, recited separately from the
    // beneficiary half so one row does not report a creditor's payment as having
    // gone to a beneficiary. Both render parenthesised.
    | 'creditor-payments'
    | 'distributed-deduction'
    | 'pending-deduction';
  /** Pre-formatted phrasing, e.g. "Proceeds received from Commonwealth Bank". */
  label: string;
  faviconUrl: string | null;
  /** `null` on an `institution-proceeds` row marked collected with no amount (→ CTA). */
  amount: number | null;
  source: 'item-sum' | 'unset' | 'n/a';
  parenthesised: boolean;
  /** Matter↔institution relationship id (NOT the institution id). */
  matterInstitutionId: number | null;
  /**
   * Global institution id — the value the drawer + `PUT /matters/:matterId/
   * institutions/:institutionId` key on. **Pending BE** (reverse brief
   * `LF-3901-fe-to-be-reverse-brief.md`, Gap 1): `matterInstitutionId` is the
   * relationship id and doesn't reconcile FE-side. Until this ships, the
   * proceeds-row institution click / "Enter collected amount" CTA are inert
   * (a no-op is safe; routing on `matterInstitutionId` would open the wrong
   * institution).
   */
  institutionId?: number | null;
  inventoryItemId: number | null;
  enterAmountCta: boolean;
}

/** 🏛️ [Firm] Trust Account row. `balance` = collected − distributed (the canonical "available"). */
export interface TrustAccountDto {
  firmName: string;
  balance: number;
  isNegativeBalance: boolean;
  helperText: string | null;
  unenteredInstitutionCount: number;
  proceeds: ProceedsRowDto[];
  /** Show the row when true (balance != 0 OR any proceeds row has a non-null amount). */
  visible: boolean;
}

/** A dual-amount row in the Today view Outstanding groups (LF-4023). */
export interface TodayItemRow {
  inventoryItemId: number;
  category: 'asset' | 'liability';
  typeSlug: string;
  institutionFaviconUrl: string | null;
  label: string;
  detail: string | null;
  notePreview: string | null;
  statusTag: OutstandingItemStatusTag;
  /** Pre-formatted chip text; `statusTag` drives only the colour. */
  statusTagLabel: string;
  /**
   * Date-of-death gross (assets) / amount owing (liabilities). `null` on an
   * asset ⇒ Unvalued (DoD "Add value" pill, LF-5546), parallel to `currentValue`.
   */
  dodValue: number | null;
  /** Current value; `null` ⇒ amber-$0 (assets); liabilities use currentBalance ?? amountOwed. */
  currentValue: number | null;
  parenthesised: boolean;
  payeeName: string | null;
  /** Non-estate recipient hint — renders "→ name". */
  recipientName: string | null;
  /**
   * Current-value confirmation state (LF-4356). `true` ⇒ Confirmed (anchored ✓);
   * `false` + a non-null `currentValue` ⇒ Estimate (leading `~`); `false` + a
   * null `currentValue` (asset) ⇒ Unvalued ("Add value" pill).
   */
  currentValueConfirmed: boolean;
  /** Confirmation provenance when `currentValueConfirmed` is true; null otherwise. */
  currentValueConfirmation: CurrentValueConfirmation | null;
  /**
   * Date-of-death-value confirmation state (LF-5546), parallel to
   * `currentValueConfirmed`. `true` ⇒ Confirmed (anchored ✓); `false` + a
   * non-null `dodValue` ⇒ Estimate (leading `~`); `false` + a null `dodValue`
   * (asset) ⇒ Unvalued ("Add value" pill). DoD is never market-priced.
   */
  dodValueConfirmed: boolean;
  /** DoD-value confirmation provenance when `dodValueConfirmed` is true; null otherwise. */
  dodValueConfirmation: CurrentValueConfirmation | null;
  /**
   * External handling governing this row (EH-DI-002), **inherited from the
   * institution** where the item has one (EH-CR-003). Distinct from
   * `InventoryItem.handling`, which is the item's OWN handler and is therefore
   * always empty for an institution-backed asset — reading that one is why the
   * Inventory page showed nothing for 14 assets sitting at three handed-over
   * institutions (VALE-1760).
   *
   * Always an object, never null — test `handling.handledBy`.
   */
  handling: Handling;
  /**
   * Whether a control on this row may write a handler to the item ITSELF
   * (EH-CR-007) — true only for the institution-less types. Stated by the BFF
   * rather than re-derived here from a list of type slugs: that list belongs to
   * the DB CHECK, and a copy of it in the client is the one that goes stale.
   *
   * A row whose `handling` is INHERITED reads false, which is what stops a
   * take-back on it writing where the database refuses.
   */
  canSetOwnHandling: boolean;
}

/** A grouped section of Today view rows with BFF-authoritative dual-column totals. */
export interface TodayGroup {
  rows: TodayItemRow[];
  dodTotal: number;
  currentTotal: number;
}

export interface TodayOutstandingDto {
  estateAssets: TodayGroup;
  estateLiabilities: TodayGroup;
  estateNet: { dodNet: number; currentNet: number };
  nonEstateAssets: TodayGroup;
  nonEstateLiabilities: TodayGroup;
  nonEstateNet: { dodNet: number; currentNet: number };
  /** `null` only when the matter has no institutions (Edge #7). */
  trustAccount: TrustAccountDto | null;
}

export interface SettledDebtRowDto {
  id: string;
  /** `null` when settled via institution payout (no inventory line). */
  inventoryItemId: number | null;
  /** `null` when settled via institution payout (no distribution row). */
  distributionId: number | null;
  /**
   * How the debt was settled. `institution-payout` and `auto-resolved` rows have no
   * distribution. `auto-resolved` covers a liability cleared outside a Vale
   * distribution — a linked asset's settlement (LF-3971) or a debt the lawyer / AI
   * import marked `resolved` because it was paid outside the estate (LF-4655).
   */
  settledVia?: 'distribution' | 'institution-payout' | 'auto-resolved';
  typeSlug: string;
  label: string;
  amount: number;
}

export interface BeneficiaryCashDistributionDto {
  distributionId: number;
  amount: number;
  distributedAt: string;
}

export interface BeneficiaryCashRowDto {
  id: string;
  contactId: number;
  contactName: string;
  cumulativeAmount: number;
  distributions: BeneficiaryCashDistributionDto[];
}

export interface InSpecieTransferRowDto {
  id: string;
  inventoryItemId: number;
  /** `null` when the transfer has no backing distribution (B14 — asset set
   *  transfer-to-beneficiary + terminal via inventory PUT). */
  distributionId: number | null;
  typeSlug: string;
  label: string;
  recipientContactName: string | null;
  transferredAt: string;
  status: 'completed';
}

/**
 * An asset disposed of with no recipient — a scrap-or-dispose vehicle (LF-4151).
 * Realises 0 cash like an in-specie transfer, but has no beneficiary, so it
 * renders in its own "Disposed Assets" sub-section with no recipient arrow.
 */
export interface DisposedAssetRowDto {
  id: string;
  inventoryItemId: number;
  typeSlug: string;
  label: string;
  disposedAt: string;
  status: 'completed';
}

export interface DistributedSectionDto {
  used: boolean;
  /**
   * Σ cash distributed (settled debts + beneficiary cash; in-specie excluded).
   * **Pending BE field** (technical v1.4) — not yet on the shipped BFF; until it
   * lands, derive from the `distributed-deduction` proceeds row's amount.
   */
  totalDistributed?: number;
  settledDebts: SettledDebtRowDto[];
  beneficiaryCashDistributions: BeneficiaryCashRowDto[];
  inSpecieTransfers: InSpecieTransferRowDto[];
  /** Disposed-of assets (scrap-or-dispose vehicles, LF-4151). May be absent on
   *  older BFF payloads — treat as `[]`. */
  disposals?: DisposedAssetRowDto[];
  notice: string | null;
}

/**
 * The on-demand "Excluded" umbrella section (LF-4576). Terminal-status items
 * (Not held / Archived / Not owed) — excluded from Outstanding/Distributed but
 * surfaced under one section when the matching Status filter is selected, each
 * row keeping its own status tag and clickable to revert.
 */
export interface ExcludedSectionDto {
  rows: TodayItemRow[];
}

/** Response of `GET /matters/:matterId/inventory-items/current-view`. No `preferredView` (toggle is localStorage-only). */
export interface CurrentViewResponseDto {
  outstanding: TodayOutstandingDto;
  distributed: DistributedSectionDto;
  /** Terminal-status items surfaced on demand (LF-4576). May be absent on older
   *  BFF payloads — treat as `{ rows: [] }`. */
  excluded?: ExcludedSectionDto;
}

/**
 * Reclassification (VALE-1387) — the wire shape of
 * `GET /matters/:matterId/inventory-items/:id/reclassification-preview`.
 *
 * A type change is not a field edit: it moves the item between the asset and
 * liability families, carries its recorded amount onto the other family's key, drops
 * the confirmed-value tick, and is refused outright once money has moved. The drawer
 * asks the server what a given change would do rather than deciding locally, because
 * the answer depends on what else on the matter already points at the item.
 */
export type ReclassificationTier = 'safe' | 'warn' | 'refuse';

export interface ReclassificationConsequence {
  /** Stable identifier to branch on. */
  code: string;
  /** The sentence to show the reader. */
  detail: string;
}

export interface ReclassificationPreview {
  fromType: string;
  toType: string;
  fromCategory: InventoryItemCategory;
  toCategory: InventoryItemCategory;
  crossesFamily: boolean;
  tier: ReclassificationTier;
  consequences: ReclassificationConsequence[];
}
